import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, Box, TextField, SxProps, Theme, Typography, useTheme, AutocompleteChangeReason, AutocompleteChangeDetails } from '@mui/material';
import DashboardOption from './DashboardOption';
import { Dashboard } from '../../../../api/dashboards';
import DashboardOptionError from './DashboardOptionError';
import { DashboardContext } from '../../DashboardContext';
import { DashboardFiltersContext } from '../filters/DashboardFiltersContext';
import { DashboardFilters } from '../../../../models/dashboard';

interface DashboardListProps {
    sx: SxProps<Theme>;
}

interface DashboardListOption {
    dashboard: Dashboard;
    label: string,
    isFavourite: boolean;
    isDefault: boolean;
    category: string;
}

const DashboardList = ({ sx }: DashboardListProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [dashboardOptions, setDashboardOptions] = useState<DashboardListOption[]>([]);
    const { currentDashboard, setCurrentDashboard, dashboards, isLoadingDashboards: isLoading, isErrorDashboards: isError, fetchDasboards } = useContext(DashboardContext);
    const { forceSetFilters, defaultFilters } = useContext(DashboardFiltersContext);
    const [currentDashboardOption, setCurrentDashboardOption] = useState<DashboardListOption | undefined>();
    const [processingData, setProcessingData] = useState<boolean>(true);
    const [open, setOpen] = useState(false);
    const closePopper = () => setOpen(false);
    const openPopper = () => setOpen(true);
    const [autoCompleteStyle, setAutocompleteStyle] = useState<SxProps<Theme>>({});
    const [inputStyle, setInputStyle] = useState<SxProps<Theme>>({});
    const GROUP_SEPARATOR = '---';

    const defaultSkeletonRows = [
        { dashboard: {dashboard: {id: 1}} as unknown as Dashboard, label: '2', isDefault: false, isFavourite: false, category: '' }, 
        { dashboard: {dashboard: {id: 2}} as unknown as Dashboard, label: '3', isDefault: false, isFavourite: false, category: '' }, 
        { dashboard: {dashboard: {id: 3}} as unknown as Dashboard, label: '1', isDefault: false, isFavourite: false, category: '' }, 
        { dashboard: {dashboard: {id: 4}} as unknown as Dashboard, label: '4', isDefault: false, isFavourite: false, category: '' }, 
        { dashboard: {dashboard: {id: 5}} as unknown as Dashboard, label: '5', isDefault: false, isFavourite: false, category: '' },
    ]

    const onChangeDashboard = useCallback((dashboard: Dashboard | undefined) => {
        forceSetFilters(() => defaultFilters);
        if(dashboard) {
            setCurrentDashboard(dashboard);
        }
    }, [setCurrentDashboard]);

    useEffect(() => {
        setProcessingData(true);
        if (!isLoading && !isError && dashboards) {
            const parsedDashboard = dashboards.map((dashboardObject) => ({
                dashboard: dashboardObject,
                label: t(`screens.dashboarding.titleDetails.${dashboardObject.dashboard.titleDetails.i18.nameKey}`),
                isDefault: dashboardObject.dashboard.isDefault,
                isFavourite: dashboardObject.dashboard.favourite,
                category: t(`screens.dashboarding.categoryDetails.${dashboardObject.dashboard.categoryDetails.i18.nameKey}`),
                categoryDescription: t(`screens.dashboarding.categoryDetails.${dashboardObject.dashboard.categoryDetails.i18.descKey}`),
            }))
                .sort((opt1, opt2) => opt1.category > opt2.category ? 1 : -1);

            const favourites = parsedDashboard.filter(option => option.isFavourite)
                .map(option => ({ ...option, category: t(`screens.dashboarding.favourite`), categoryDescription: t(`screens.dashboarding.favourite_description`)}));

            const allOptionsIncludingFavourites = [...favourites, ...parsedDashboard];

            let defaultDashboard = dashboards.filter(
                dashboard => dashboard.dashboard.isDefault
            )[0];

            if(!defaultDashboard) {
                [ defaultDashboard ] = dashboards;
            }

            if(!currentDashboard) setCurrentDashboard(defaultDashboard);
            setCurrentDashboardOption({
                dashboard: defaultDashboard,
                label: t(`screens.dashboarding.titleDetails.${defaultDashboard.dashboard.titleDetails.i18.nameKey}`),
                isDefault: defaultDashboard.dashboard.isDefault,
                isFavourite: defaultDashboard.dashboard.favourite,
                category: t(`screens.dashboarding.categoryDetails.${defaultDashboard.dashboard.categoryDetails.i18.nameKey}`),
            });

            setDashboardOptions(allOptionsIncludingFavourites);
            
            setProcessingData(false);
            setAutocompleteStyle({});
            setInputStyle({});
        }
        if (isError) {
            setProcessingData(false);
            setDashboardOptions([defaultSkeletonRows[0]]);
            setAutocompleteStyle({ 
                border: `solid 1px ${theme.palette.error.dark}`,
                borderRadius: 1,
            });
            setInputStyle({ 
                input: {
                "&::placeholder": {
                    color: theme.palette.error.dark,
                    opacity: '1',
                }
            }});
        }
    }, [dashboards, isLoading, isError, setCurrentDashboard]);

    useEffect(() => {
         if (currentDashboard && dashboardOptions.length > 0) {
        const matchedOption = dashboardOptions.find(
            (opt) => opt.dashboard.dashboard.id === currentDashboard.dashboard.id
        );

        if (matchedOption) {
            setCurrentDashboardOption(matchedOption);
        } else {
            setCurrentDashboardOption({
                dashboard: currentDashboard,
                label: currentDashboard.dashboard.titleDetails.name,
                isDefault: currentDashboard.dashboard.isDefault,
                isFavourite: currentDashboard.dashboard.favourite,
                category: currentDashboard.dashboard.categoryDetails.i18.nameKey,
            });
        }
    }
    }, [currentDashboard, dashboardOptions])

     return (
        <Box sx={sx}>
            {processingData &&
                <Autocomplete
                    disableClearable
                    data-test-id="dashboardlist-loading"
                    sx={sx}
                    renderInput={(params) => <TextField {...params} label= {t('screens.dashboarding.loading')} data-test-id="dashboardlist-input-loading" />}
                    options={defaultSkeletonRows}
                    renderOption={(props, option) => 
                        <DashboardOption
                            props={props}
                            id={option.dashboard.dashboard.id}
                            name={option.label}
                            isFavourite={option?.isFavourite}
                            isDefault={option?.isDefault}
                            isLoading={processingData}
                        />
                    }
                    open={open}
                    onOpen={openPopper}
                    onClose={closePopper}
                />
            
            }
            {!processingData &&
                <Autocomplete
                    data-testid="dashboardlist-autocomplete"
                    disableClearable
                    disablePortal
                    sx={autoCompleteStyle}
                    value={currentDashboardOption}
                    onChange={(event, newValue) => {
                        setCurrentDashboardOption(newValue ?? undefined);
                        onChangeDashboard(newValue?.dashboard);
                    }}
                    isOptionEqualToValue={(option, value) =>
                        option.dashboard.dashboard.id === value.dashboard.dashboard.id
                    }
                    renderInput={(params) =>
                        <TextField {...params} placeholder={isError?t('screens.dashboarding.dataFailed'):''}
                            color={isError ? 'error' : 'primary'}
                            sx={inputStyle}
                            data-testid="dashboardlist-input"
                        />
                    }
                      
                    options={dashboardOptions}
                    groupBy={(option) => `${option.category}${GROUP_SEPARATOR}${option.label}`}
                    renderGroup={(params) => 
                        <Box key={`container-${params.group}`} sx={{borderLeft: 'solid 0.25px', borderRight: 'solid 0.25px', borderTop: 'solid 0.25px', borderColor: theme?.palette?.grey?.['12p'] }}>
                            {!isError && <Typography key={`title-${params.group}`} sx={{font: 'opensans', fontWeight: '600', fontSize:'16', paddingLeft: '16px', marginBottom: '4px', paddingTop: '16px'}} >{params.group.split(GROUP_SEPARATOR)[0]}</Typography> }
                            {!isError && <Typography key={`${params.key}-description-${params.group}}`} sx={{font: 'opensans', fontSize:'14', paddingLeft: '16px', marginBottom: '2px', color: 'text.secondary' }}>{params.group.split(GROUP_SEPARATOR)[1]}</Typography> }
                            <Box key={`elements-${params.group}`} >{params.children}</Box>
                        </Box>
                    }
                    renderOption={(props, option) => {
                        if(isError) return (
                        <DashboardOptionError 
                            onDismiss={closePopper}    
                            onRetry={fetchDasboards}
                        />);
                        return (
                            <Box key={option.dashboard.dashboard.id}>
                            <DashboardOption
                                props={props}
                                id={`${option.category}-${option.dashboard.dashboard.id}`}
                                name={option.label}
                                isFavourite={option?.isFavourite}
                                isDefault={option?.isDefault}
                            />
                            </Box>
                        )
                        }
                    }
                    open={open}
                    onOpen={openPopper}
                    onClose={closePopper}
                />
            }
        </Box>
    );
};

export default DashboardList;
